import { HStack, VStack } from '@sgme/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { NegociationTerminationFeedback } from '@/App/VoiceTimestamping/NegociationTerminationFeedback.tsx';
import { useTrackEvent } from '@/App/VoiceTimestamping/useTrackEvent.ts';
import { pretradeApi } from '@/store/pretrade/api/index.ts';
import type { Negociation, NegociationId } from '@/store/pretrade/api/types.ts';

type Props = {
  negociation: Negociation;
  onHasError: (hasError: boolean) => void;
  onNegociationEnded: (id: NegociationId, dealDone: boolean) => void;
};

export const NegociationCard = ({
  negociation: { id, status, createdAt, quotes },
  onHasError,
  onNegociationEnded,
}: Props) => {
  const trackEvent = useTrackEvent();

  const [createQuote] = pretradeApi.useCreateQuoteMutation();
  const [endNegociation] = pretradeApi.useEndNegociationMutation();

  const [isCreatingQuote, setIsCreatingQuote] = useState(false);

  const quotesContainerRef = useRef<HTMLDivElement>(null);
  const quotesListRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const quotesListElement = quotesListRef.current;
    const quotesContainerElement = quotesContainerRef.current;
    if (!quotesListElement || !quotesContainerElement) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      quotesContainerRef.current?.scrollTo({ top: quotesContainerRef.current.scrollHeight, behavior: 'smooth' });
    });
    observer.observe(quotesListElement);

    return () => {
      observer.unobserve(quotesListElement);
    };
  }, []);

  const requestStartQuote = useCallback(async () => {
    const createdAt = Date.now();

    trackEvent('Record timestamp');

    setIsCreatingQuote(true);
    const { error } = await createQuote({ negociationId: id, creationTimestamp: createdAt });
    setIsCreatingQuote(false);

    if (!error) {
      quotes.push({ createdAt });
    }
    onHasError(!!error);
  }, [createQuote, onHasError, quotes.push, id]);

  const endQuoteWithDealDone = useCallback(async () => {
    const createdAt = Date.now();

    trackEvent('Done deal');

    const { error } = await endNegociation({ negociationId: id, terminationTimestamp: createdAt, dealDone: true });

    onHasError(!!error);

    if (!error) {
      onNegociationEnded(id, true);
    }
  }, [endNegociation, onHasError, id, onNegociationEnded]);

  const endQuoteWithDealMissed = useCallback(async () => {
    const createdAt = Date.now();

    trackEvent('Missed deal');

    const { error } = await endNegociation({ negociationId: id, terminationTimestamp: createdAt, dealDone: false });

    onHasError(!!error);

    if (!error) {
      onNegociationEnded(id, false);
    }
  }, [endNegociation, onHasError, id, onNegociationEnded]);

  if (status !== 'in progress') {
    return <NegociationTerminationFeedback id={id} status={status} />;
  }

  return (
    <div className="card card-bordered mx-1">
      <div className="card-header fw-bold p-2 pb-1">
        <FormattedMessage id="voiceTimestamping.panel.negociation.title" values={{ id }} />
      </div>
      <div className="card-body px-2 py-1">
        <FormattedMessage
          id="voiceTimestamping.panel.negociation.body.createdAt"
          values={{
            dateTime: <FormattedDate value={createdAt} dateStyle="medium" timeStyle="short" />,
          }}
        />
        <div
          ref={quotesContainerRef}
          className="overflow-y-auto"
          style={{ maxHeight: '200px', scrollbarWidth: 'thin' }}
        >
          <ol ref={quotesListRef} className="list-group">
            {quotes.map((quote, index) => (
              <li key={quote.createdAt} className="list-group-item p-0 pt-1">
                <FormattedMessage
                  id="voiceTimestamping.panel.negociation.body.quote"
                  values={{
                    quoteIndex: index + 1,
                    dateTime: <FormattedDate value={quote.createdAt} dateStyle="medium" timeStyle="short" />,
                  }}
                />
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="card-footer p-2">
        <VStack>
          <button
            type="button"
            className="btn btn-outline-primary w-100 mb-2"
            onClick={requestStartQuote}
            disabled={isCreatingQuote}
          >
            <FormattedMessage id="voiceTimestamping.panel.addQuote" />
          </button>
          <HStack>
            <button
              type="button"
              className="btn btn-success flex-grow-1 me-2"
              onClick={endQuoteWithDealDone}
              disabled={quotes.length === 0}
            >
              <FormattedMessage id="voiceTimestamping.panel.done" />
            </button>
            <button type="button" className="btn btn-danger flex-grow-1" onClick={endQuoteWithDealMissed}>
              <FormattedMessage id="voiceTimestamping.panel.missed" />
            </button>
          </HStack>
        </VStack>
      </div>
    </div>
  );
};
